import {
  fromCanonicalKg,
  isCalibrationOver,
  nearestLoadable,
  nextCalibrationKg,
  nextLoadableUp,
  parseLocaleNumber,
  plateLoadModeFor,
  toCanonicalKg,
  type EffortScale,
  type Exercise,
  type LoadableContext,
} from '@heraion/shared';

import { calibrationSet } from './state/calibrationReducer';
import { savedRirOf } from './state/canonicalRir';
import type { DraftEntry, DraftSet } from './state/loggerDraftTypes';

// #353: the logger's side of the MAX calibration. The rule lives in
// @heraion/shared (calibration.ts); this reads the draft's display strings
// into canonical kg and back, once.

/** What the athlete can build: their plate steps (`useRoundingContextRef`,
 *  undefined until read) and the exercise's catalog loading shape. */
export type CalibrationLoadable = {
  ctx: LoadableContext | undefined;
  barLoading: Exercise['barLoading'];
  /** #49: a dumbbell ramps on the rack's step, not on plates. */
  equipment?: Exercise['equipment'];
};

// UP to a rung the athlete can build, never down: the ramp must rise. The
// nearest rung when it is not below the load (on a rung, or past the half
// step, or under the FIRST step, where `roundLoadable` would hand the input
// back unbuilt), else the next one up.
const loadableUp = (kg: number, unit: DraftSet['unit'], l: CalibrationLoadable) => {
  const mode = plateLoadModeFor(l.barLoading, l.equipment);
  const near = nearestLoadable(kg, unit, l.ctx, mode);
  return near >= kg - 1e-6 ? near : nextLoadableUp(kg, unit, l.ctx, 1, mode);
};

/** The set the ramp adds after the athlete logs `loggedIdx`, or null when the
 *  ramp stops there. Read against the PRE-dispatch entry, like the rest
 *  transition: the set being logged still reads pending. `logged` is what the
 *  same tap records (the meter's RIR, a fail). */
export function nextCalibrationSet(
  entry: DraftEntry,
  loggedIdx: number,
  logged: { rir?: number; fail?: boolean },
  loadable: CalibrationLoadable,
  effortScale: EffortScale = 'rir',
): DraftSet | null {
  if (entry.calibration !== 'accepted') return null;
  const set = entry.sets[loggedIdx];
  if (set?.calibration !== true || set.completedAt !== null) return null;
  // Only the ramp's newest set extends it. A PENDING one after it is a
  // leftover of an uncompleted set (#468), not a successor: it goes below.
  if (
    entry.sets
      .slice(loggedIdx + 1)
      .some((s) => s.calibration === true && s.completedAt !== null)
  ) {
    return null;
  }
  const rir = logged.rir ?? savedRirOf(set.rir, set.rpe, effortScale);
  const setsDone =
    entry.sets.filter((s) => s.calibration === true && s.completedAt !== null)
      .length + 1;
  if (isCalibrationOver({ rir, fail: logged.fail === true, setsDone })) {
    return null;
  }
  const typed = parseLocaleNumber(set.weight);
  const reps = parseLocaleNumber(set.reps);
  const kg =
    set.weight.trim() !== '' && Number.isFinite(typed) && Number.isFinite(reps)
      ? nextCalibrationKg({ weightKg: toCanonicalKg(typed, set.unit), reps, rir })
      : null;
  // No load to start from: the next set comes blank, still part of the ramp.
  const weight =
    kg === null ? '' : String(fromCanonicalKg(loadableUp(kg, set.unit, loadable), set.unit));
  return calibrationSet(
    entry,
    set.unit,
    weight,
    kg === null ? undefined : 'effort-from-history',
  );
}

// A completed ramp set with a load and a rep: what a MAX proposal can read.
const rampHasProposableSet = (ramp: readonly DraftSet[]): boolean =>
  ramp.some((s) => {
    if (s.completedAt === null) return false;
    const w = parseLocaleNumber(s.weight);
    const r = parseLocaleNumber(s.reps);
    return Number.isFinite(w) && w > 0 && Number.isFinite(r) && r >= 1;
  });

export type CalibrationCueKind = 'offer' | 'toCalibrate' | 'first' | 'ramp' | 'done';

/** What the card says about a calibration, or null for nothing. `hasMax` is
 *  the live MAX map's answer for this exercise, null while it is unknown:
 *  an offer made at start against a map that failed to load must not stand
 *  once the map shows a MAX, nor before it shows anything. */
export function calibrationCueOf(
  entry: DraftEntry,
  hasMax: boolean | null,
): { kind: CalibrationCueKind; setNumber: number } | null {
  const ramp = entry.sets.filter((s) => s.calibration === true);
  const done = ramp.filter((s) => s.completedAt !== null).length;
  if (entry.calibration === 'accepted') {
    if (ramp.length === 0) return null;
    if (done === ramp.length) {
      // "The new MAX waits" only when a done set can propose one.
      return rampHasProposableSet(ramp)
        ? { kind: 'done', setNumber: done }
        : { kind: 'toCalibrate', setNumber: 0 };
    }
    return { kind: done === 0 ? 'first' : 'ramp', setNumber: done + 1 };
  }
  if (entry.calibration === undefined || hasMax !== false) return null;
  const started = entry.sets.some((s) => s.completedAt !== null);
  return entry.calibration === 'offered' && !started
    ? { kind: 'offer', setNumber: 0 }
    : { kind: 'toCalibrate', setNumber: 0 };
}
